import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Stethoscope, Plus, BadgeCheck, Ban, Copy } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSEO } from "@/lib/seo";
import AdminTabs from "@/components/AdminTabs";

function copyToClipboard(text) {
  if (!navigator?.clipboard) return;
  navigator.clipboard.writeText(text).catch(() => {});
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

export default function AdminDoctorCodes() {
  useSEO({ title: "Admin · Doctor hardship codes", noindex: true });

  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    doctor_name: "",
    doctor_email: "",
    doctor_license: "",
    max_redemptions: 25,
    discount_type: "hardship_free",
    notes: "",
  });
  const [lastMinted, setLastMinted] = useState(null);

  const reload = async () => {
    setError("");
    setLoading(true);
    try {
      const r = await api.get("/admin/doctor-codes");
      setCodes(r.data?.codes || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.doctor_name.trim()) {
      setError("Doctor name is required");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const r = await api.post("/admin/doctor-codes", {
        doctor_name: form.doctor_name.trim(),
        doctor_email: form.doctor_email.trim() || null,
        doctor_license: form.doctor_license.trim() || null,
        max_redemptions: Number(form.max_redemptions) || 25,
        discount_type: form.discount_type,
        notes: form.notes.trim() || null,
      });
      setLastMinted(r.data);
      setForm({
        doctor_name: "",
        doctor_email: "",
        doctor_license: "",
        max_redemptions: 25,
        discount_type: "hardship_free",
        notes: "",
      });
      setShowForm(false);
      reload();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async (code) => {
    if (!window.confirm(`Deactivate ${code}? Existing redemptions stay valid; no new ones can be made.`)) return;
    try {
      await api.post(`/admin/doctor-codes/${code}/deactivate`);
      reload();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const activeCount = codes.filter((c) => c.active).length;
  const totalRedemptions = codes.reduce((sum, c) => sum + (c.redemptions || 0), 0);

  return (
    <div className="min-h-screen bg-[color:var(--cream)] px-5 sm:px-10 py-6">
      <div className="max-w-5xl mx-auto">
        <Link to="/admin/region" className="pill-btn mb-4 inline-flex" data-testid="back-to-admin">
          <ArrowLeft size={14} /> Back
        </Link>
        <AdminTabs current="doctor-codes" />

        <h1 className="display text-3xl sm:text-4xl font-extrabold flex items-center gap-3 mb-2">
          <Stethoscope size={32} className="text-[color:var(--coral)]" />
          Doctor hardship codes
        </h1>
        <p className="text-[color:var(--ink-soft)] mb-6 max-w-3xl">
          In-network doctors mint codes here, give them to patients with verified
          financial hardship (welfare, SNAP, etc.), and patients redeem on signup
          for a 12-month sponsored Starter plan ($0 to family).
        </p>

        {/* Summary tiles */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="brutal-card bg-white p-3" data-testid="codes-summary-active">
            <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--ink-soft)] font-bold">Active</div>
            <div className="display text-3xl font-extrabold">{activeCount}</div>
          </div>
          <div className="brutal-card bg-white p-3" data-testid="codes-summary-total">
            <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--ink-soft)] font-bold">Total codes</div>
            <div className="display text-3xl font-extrabold">{codes.length}</div>
          </div>
          <div className="brutal-card bg-[color:var(--gold)] p-3" data-testid="codes-summary-redemptions">
            <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--ink)] font-bold">Redemptions</div>
            <div className="display text-3xl font-extrabold">{totalRedemptions}</div>
          </div>
        </div>

        {error && (
          <div className="brutal-card bg-[#FFE3E0] text-[color:var(--coral-deep)] p-3 mb-4" data-testid="codes-error">
            {error}
          </div>
        )}
        {lastMinted && (
          <div className="brutal-card bg-[color:var(--gold)] p-4 mb-4 flex items-start gap-3" data-testid="codes-last-minted">
            <BadgeCheck size={20} />
            <div className="flex-1">
              <div className="text-sm font-bold mb-1">Code minted for {lastMinted.doctor_name}:</div>
              <div className="font-mono text-lg select-all">{lastMinted.code}</div>
              <div className="text-xs text-[color:var(--ink-soft)] mt-1">
                Email this to the doctor. Patient enters it on /register.
              </div>
            </div>
            <button
              type="button"
              onClick={() => copyToClipboard(lastMinted.code)}
              className="pill-btn"
              data-testid="copy-last-minted"
            >
              <Copy size={12} /> Copy
            </button>
          </div>
        )}

        {/* Mint form */}
        {!showForm ? (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="pill-btn primary mb-6"
            data-testid="mint-toggle"
          >
            <Plus size={14} /> Mint a new code
          </button>
        ) : (
          <form onSubmit={submit} className="brutal-card bg-white p-5 mb-6 space-y-3" data-testid="mint-form">
            <h2 className="display text-xl font-extrabold mb-2">Mint a new code</h2>
            <label className="block">
              <span className="display text-xs uppercase tracking-[0.18em] font-bold mb-1 block">Doctor name *</span>
              <input
                className="brutal-input"
                value={form.doctor_name}
                onChange={(e) => setForm({ ...form, doctor_name: e.target.value })}
                placeholder="Dr. Jane Chen"
                required
                data-testid="mint-doctor-name"
              />
            </label>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="display text-xs uppercase tracking-[0.18em] font-bold mb-1 block">Doctor email</span>
                <input
                  className="brutal-input"
                  type="email"
                  value={form.doctor_email}
                  onChange={(e) => setForm({ ...form, doctor_email: e.target.value })}
                  placeholder="optional"
                  data-testid="mint-doctor-email"
                />
              </label>
              <label className="block">
                <span className="display text-xs uppercase tracking-[0.18em] font-bold mb-1 block">License #</span>
                <input
                  className="brutal-input"
                  value={form.doctor_license}
                  onChange={(e) => setForm({ ...form, doctor_license: e.target.value })}
                  placeholder="optional"
                  data-testid="mint-doctor-license"
                />
              </label>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="display text-xs uppercase tracking-[0.18em] font-bold mb-1 block">Max redemptions</span>
                <input
                  className="brutal-input"
                  type="number"
                  min={1}
                  max={10000}
                  value={form.max_redemptions}
                  onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })}
                  data-testid="mint-max-redemptions"
                />
              </label>
              <label className="block">
                <span className="display text-xs uppercase tracking-[0.18em] font-bold mb-1 block">Discount type</span>
                <select
                  className="brutal-input"
                  value={form.discount_type}
                  onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                  data-testid="mint-discount-type"
                >
                  <option value="hardship_free">Hardship — 12 months free</option>
                  <option value="fifty_off">50% off any yearly plan</option>
                </select>
              </label>
            </div>
            <label className="block">
              <span className="display text-xs uppercase tracking-[0.18em] font-bold mb-1 block">Notes</span>
              <textarea
                className="brutal-input"
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Internal note (optional)"
                data-testid="mint-notes"
              />
            </label>
            <div className="flex gap-2">
              <button type="submit" className="pill-btn primary" disabled={busy} data-testid="mint-submit">
                {busy ? "Minting…" : "Mint code"}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="pill-btn">
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Codes list */}
        {loading ? (
          <p>Loading…</p>
        ) : codes.length === 0 ? (
          <div className="brutal-card bg-white p-6 text-center text-[color:var(--ink-soft)]">
            No doctor codes yet. Mint your first one above.
          </div>
        ) : (
          <div className="brutal-card bg-white p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[color:var(--paper-muted)] border-b border-[color:var(--ink-soft)]/30">
                <tr>
                  <th className="text-left p-3 font-bold display text-xs uppercase tracking-[0.15em]">Code</th>
                  <th className="text-left p-3 font-bold display text-xs uppercase tracking-[0.15em]">Doctor</th>
                  <th className="text-left p-3 font-bold display text-xs uppercase tracking-[0.15em]">Type</th>
                  <th className="text-left p-3 font-bold display text-xs uppercase tracking-[0.15em]">Redeemed</th>
                  <th className="text-left p-3 font-bold display text-xs uppercase tracking-[0.15em]">Created</th>
                  <th className="text-left p-3 font-bold display text-xs uppercase tracking-[0.15em]">Status</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {codes.map((c) => (
                  <tr key={c.code} className="border-b border-[color:var(--ink-soft)]/15" data-testid={`code-row-${c.code}`}>
                    <td className="p-3 font-mono select-all">{c.code}</td>
                    <td className="p-3">
                      <div className="font-bold">{c.doctor_name}</div>
                      {c.doctor_email && <div className="text-xs text-[color:var(--ink-soft)]">{c.doctor_email}</div>}
                    </td>
                    <td className="p-3">
                      <span className="text-xs">
                        {c.discount_type === "hardship_free" ? "12-mo free" : "50% off yearly"}
                      </span>
                    </td>
                    <td className="p-3">
                      <span className="font-bold">{c.redemptions || 0}</span>
                      <span className="text-[color:var(--ink-soft)]"> / {c.max_redemptions}</span>
                    </td>
                    <td className="p-3 text-xs text-[color:var(--ink-soft)]">{fmtDate(c.created_at)}</td>
                    <td className="p-3">
                      {c.active ? (
                        <span className="text-xs text-green-700 font-bold">Active</span>
                      ) : (
                        <span className="text-xs text-[color:var(--ink-soft)]">Inactive</span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => copyToClipboard(c.code)}
                          className="pill-btn text-xs px-2 py-1"
                          data-testid={`copy-${c.code}`}
                          title="Copy code"
                        >
                          <Copy size={10} />
                        </button>
                        {c.active && (
                          <button
                            type="button"
                            onClick={() => deactivate(c.code)}
                            className="pill-btn text-xs px-2 py-1"
                            data-testid={`deactivate-${c.code}`}
                            title="Deactivate"
                          >
                            <Ban size={10} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
